import { useApolloClient, useQuery } from '@apollo/client';
import { useCallback, useEffect, useState, useRef } from 'react';
import { LIST_ANALYSES_VIEW, LIST_PROFESSIONALS_QUERY } from '../../services/graphql/queries/admin';
import { GET_PROFESSIONAL_LIST_CLIENTS_LIGHT } from '../../services/graphql/queries/professional';
import { useAdminSignInAsProfessional } from './useAdminProfessional';

export default function useAdminGetClients(currentPage: number = 1, perPage: number = 15, search: string = '') {
  const [allClients, setAllClients] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const apolloClient = useApolloClient();
  const { signInAsProfessional } = useAdminSignInAsProfessional();
  
  const clientMapRef = useRef<Map<string, any>>(new Map());
  const isEnrichingRef = useRef<boolean>(false);

  // 1. Initial Sweep: Get names from logs (HMA + HRV)
  const { data: hmaData, loading: loadingHma } = useQuery(LIST_ANALYSES_VIEW, {
    variables: { viewType: 'hma', page: 1, perPage: 1000 }
  });
  
  const { data: hrvData, loading: loadingHrv } = useQuery(LIST_ANALYSES_VIEW, {
    variables: { viewType: 'hrv', page: 1, perPage: 1000 }
  });

  const { data: profData, loading: loadingProfs } = useQuery(LIST_PROFESSIONALS_QUERY, {
    variables: { page: 1, perPage: 1000 }
  });

  // Unique Key Generator (StableId + ProfCode) to avoid overwriting clients with same name.
  const getUniqueKey = (stableId: string | number | undefined, fullName: string, profCode: string) => {
    const idPart = String(stableId || '').trim();
    if (idPart) {
      return `${idPart}|${(profCode || '').toLowerCase().trim()}`;
    }

    return `${fullName.toLowerCase().trim()}|${(profCode || '').toLowerCase().trim()}`;
  };

  // INITIAL SWEEP: Populate with names (placeholders)
  useEffect(() => {
    if (loadingHma || loadingHrv || loadingProfs) return;

    const initialData = [...(hmaData?.listAnalysesView?.data || []), ...(hrvData?.listAnalysesView?.data || [])];
    let mapUpdated = false;

    initialData.forEach(item => {
      const stableId = item.clientGid || item.client;
      const key = getUniqueKey(stableId, item.client, item.professionalCode);
      if (!clientMapRef.current.has(key)) {
        const nameParts = item.client.split(' ');
        const firstName = nameParts[0] || '';
        const familyName = nameParts.slice(1).join(' ') || '';

        clientMapRef.current.set(key, {
           id: item.clientGid || key,
           firstName,
           familyName,
           fullName: item.client,
           professionalName: item.professional,
           professionalCode: item.professionalCode,
           email: '...', // Loading placeholder
           accountStatus: 'loading',
           type: 'Full',
           createdAt: item.date 
        });
        mapUpdated = true;
      }
    });

    if (mapUpdated) {
      setAllClients(Array.from(clientMapRef.current.values()));
    }
    setLoading(false);
  }, [hmaData, hrvData, loadingHma, loadingHrv, loadingProfs]);

  // BACKGROUND ENRICHMENT: Replace placeholders with real details
  const enrichClientsData = useCallback(async () => {
    if (loadingProfs || !profData?.listProfessionals?.data || isEnrichingRef.current) return;

    try {
      isEnrichingRef.current = true;
      const professionals = profData.listProfessionals.data;
      
      console.log(`useAdminGetClients - Consolidating clients for ${professionals.length} professionals`);

      for (const prof of professionals) {
        if (!prof.email) continue;
        
        try {
          const result = await signInAsProfessional(prof.email);
          const tempToken = result?.token;

          if (tempToken) {
            const { data } = await apolloClient.query({
              query: GET_PROFESSIONAL_LIST_CLIENTS_LIGHT,
              variables: { page: 1, perPage: 1000 },
              context: { headers: { Authorization: `Bearer ${tempToken}` } },
              fetchPolicy: 'network-only'
            });

            const enrichedBatch = data?.professionalListClients?.data || [];
            if (enrichedBatch.length > 0) {
              let mapUpdated = false;
              
              enrichedBatch.forEach((client: any) => {
                const fullName = `${client.firstName} ${client.familyName}`;
                const stableId = client?.id || client?.gid || fullName;
                const key = getUniqueKey(stableId, fullName, prof.code);
                const existing = clientMapRef.current.get(key);
                
                // Overwrite the placeholder row with real system data
                clientMapRef.current.set(key, {
                  ...(existing || {}),
                  ...client,
                  fullName, // Ensure normalized
                  professionalName: `${prof.firstName} ${prof.familyName}`,
                  professionalCode: prof.code,
                  type: prof.type?.replace('ProfessionalType', '') || 'Full'
                });
                mapUpdated = true;
              });

              if (mapUpdated) {
                setAllClients(prev => {
                  const updated = Array.from(clientMapRef.current.values());
                  // Sort by createdAt descending
                  return updated.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
                });
              }
            }
          }
        } catch (err) {
          console.error(`Failed enrichment for ${prof.email}:`, err);
        }
      }
    } catch (error) {
      console.error('Error in consolidation loop:', error);
    } finally {
      // Cleanup: Remove any clients who were in analysis logs but were NOT found in actual professional client lists
      setAllClients(prev => prev.filter(c => c.email !== '...'));
      isEnrichingRef.current = false;
    }
  }, [profData, loadingProfs, signInAsProfessional, apolloClient]);

  useEffect(() => {
    if (!loadingHma && !loadingHrv && !loadingProfs) {
      enrichClientsData();
    }
  }, [loadingHma, loadingHrv, loadingProfs, enrichClientsData]);

  // Handle Search
  const filteredClients = allClients.filter(client => {
    const fullName = (client.fullName || '').toLowerCase();
    const email = (client.email || '').toLowerCase();
    const searchLower = search.toLowerCase();
    return fullName.includes(searchLower) || email.includes(searchLower);
  });

  return {
    data: filteredClients,
    loading: loading || loadingHma || loadingHrv,
    total: filteredClients.length,
    refetch: enrichClientsData,
    perPage: 15
  };
}
